import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { cn } from "../../../ui/utils/cn";
import { Emoji } from "../../../ui/components/Emoji";
import { Button } from "../../../ui/components/Button";
import {
  useAdminListEmojisQuery,
  useAdminEmojiStatsQuery,
  useAdminCreateEmojiMutation,
  useAdminUpdateEmojiMutation,
  useAdminDeleteEmojiMutation,
  useAdminReserveEmojiMutation,
  useAdminReleaseEmojiMutation,
  useAdminSyncReservationsMutation,
  useAdminListCandidatesQuery,
  useAdminPromoteCandidateMutation,
  useAdminDismissCandidateMutation,
} from "../api/emojiApi";
import { EMOJI_RESERVATION_KINDS } from "../types/emojiTypes";
import type {
  AdminEmojiRow,
  EmojiStatus,
  EmojiOwnershipState,
  EmojiReservationKind,
  SyncReservationsReport,
} from "../types/emojiTypes";

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

const StatPill: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({
  label,
  value,
  className,
}) => (
  <div
    className={cn(
      "flex items-baseline gap-1 rounded-sm border-brutal border-ink bg-white px-2.5 py-1",
      className,
    )}
  >
    <span className="text-sm font-bold">{value}</span>
    <span className="text-xs text-gray-600">{label}</span>
  </div>
);

const EmojisAdminPage: React.FC = () => {
  /* ---- filters (search is debounced: searchInput is live, search is applied) ---- */
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EmojiStatus | "">("");
  const [state, setState] = useState<EmojiOwnershipState | "">("");
  const [category, setCategory] = useState("");
  const [reservedKind, setReservedKind] = useState<EmojiReservationKind | "">("");
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // any filter change resets pagination — adjusted during render so the query
  // below never fires for a stale (new filter, old offset) combination
  const filterKey = [search, status, state, category, reservedKind].join("\u0000");
  const prevFilterKey = useRef(filterKey);
  if (prevFilterKey.current !== filterKey) {
    prevFilterKey.current = filterKey;
    if (offset !== 0) setOffset(0);
  }

  /* ---- queries ---- */
  const { data, isLoading, isFetching } = useAdminListEmojisQuery({
    ...(status ? { status } : {}),
    ...(state ? { state } : {}),
    ...(category ? { category } : {}),
    ...(search ? { search } : {}),
    ...(reservedKind ? { reservedKind } : {}),
    limit: PAGE_SIZE,
    offset,
  });
  const { data: statsData } = useAdminEmojiStatsQuery();
  const { data: candData } = useAdminListCandidatesQuery();

  const [createEmoji, { isLoading: creating }] = useAdminCreateEmojiMutation();
  const [updateEmoji] = useAdminUpdateEmojiMutation();
  const [deleteEmoji] = useAdminDeleteEmojiMutation();
  const [reserveEmoji, { isLoading: reserving }] = useAdminReserveEmojiMutation();
  const [releaseEmoji] = useAdminReleaseEmojiMutation();
  const [syncReservations, { isLoading: syncing }] = useAdminSyncReservationsMutation();
  const [promoteCandidate] = useAdminPromoteCandidateMutation();
  const [dismissCandidate] = useAdminDismissCandidateMutation();

  const entries: AdminEmojiRow[] = data?.entries ?? [];
  const total = data?.total ?? 0;
  const stats = statsData?.stats;
  const candidates = candData?.candidates ?? [];

  const categoryOptions = [...(stats?.byCategory ?? [])].sort((a, b) =>
    a.category.localeCompare(b.category),
  );

  /* ---- pagination ---- */
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + entries.length, total);
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  /* ---- create form ---- */
  const [newEmoji, setNewEmoji] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newName, setNewName] = useState("");

  const handleCreate = async (): Promise<void> => {
    if (!newEmoji.trim() || !newCategory.trim()) {
      toast.error("Emoji and category are required.");
      return;
    }
    try {
      await createEmoji({
        emoji: newEmoji.trim(),
        category: newCategory.trim(),
        name: newName.trim() || null,
      }).unwrap();
      toast.success(`Added ${newEmoji} to the registry.`);
      setNewEmoji("");
      setNewName("");
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to add emoji.");
    }
  };

  /* ---- row actions ---- */
  const toggleStatus = async (row: AdminEmojiRow): Promise<void> => {
    const next: EmojiStatus = row.status === "active" ? "retired" : "active";
    try {
      await updateEmoji({ id: row.id, body: { status: next } }).unwrap();
      toast.success(`${row.emoji} ${next === "retired" ? "retired" : "reactivated"}.`);
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to update.");
    }
  };

  const editCategory = async (row: AdminEmojiRow): Promise<void> => {
    const cat = window.prompt(`Category for ${row.emoji}`, row.category);
    if (!cat || cat.trim() === row.category) return;
    try {
      await updateEmoji({ id: row.id, body: { category: cat.trim() } }).unwrap();
      toast.success(`Updated category for ${row.emoji}.`);
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to update.");
    }
  };

  const remove = async (row: AdminEmojiRow): Promise<void> => {
    if (!window.confirm(`Delete ${row.emoji} from the registry?`)) return;
    try {
      await deleteEmoji({ id: row.id }).unwrap();
      toast.success(`Deleted ${row.emoji}.`);
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to delete (owned emojis must be retired).");
    }
  };

  const release = async (row: AdminEmojiRow): Promise<void> => {
    const what = row.reservation
      ? `${row.reservation.kind}${row.reservation.label ? ` "${row.reservation.label}"` : ""} reservation`
      : "reservation";
    if (!window.confirm(`Release the ${what} on ${row.emoji}?`)) return;
    try {
      await releaseEmoji({ id: row.id }).unwrap();
      toast.success(`Released ${row.emoji} back to the pool.`);
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to release.");
    }
  };

  const forceRelease = async (row: AdminEmojiRow): Promise<void> => {
    const owner = row.ownership.ownerUsername ?? "the owner";
    if (
      !window.confirm(
        `${row.emoji} is ${owner}'s SIGNATURE emoji. Force-releasing it takes it away from them.`,
      )
    ) {
      return;
    }
    if (
      !window.confirm(
        `Are you absolutely sure? ${owner} will permanently lose ${row.emoji} as their signature emoji. This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await releaseEmoji({ id: row.id, force: true }).unwrap();
      toast.success(`Force-released ${row.emoji} — ${owner} no longer owns it.`);
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to force-release.");
    }
  };

  /* ---- reserve dialog ---- */
  const [reserveTarget, setReserveTarget] = useState<AdminEmojiRow | null>(null);
  const [resKind, setResKind] = useState<EmojiReservationKind>("card");
  const [resRefId, setResRefId] = useState("");
  const [resLabel, setResLabel] = useState("");
  const [resNote, setResNote] = useState("");

  const openReserve = (row: AdminEmojiRow): void => {
    setResKind("card");
    setResRefId("");
    setResLabel("");
    setResNote("");
    setReserveTarget(row);
  };

  const submitReserve = async (): Promise<void> => {
    if (!reserveTarget) return;
    try {
      await reserveEmoji({
        id: reserveTarget.id,
        body: {
          kind: resKind,
          refId: resRefId.trim() || null,
          label: resLabel.trim() || null,
          note: resNote.trim() || null,
        },
      }).unwrap();
      toast.success(`Reserved ${reserveTarget.emoji} (${resKind}).`);
      setReserveTarget(null);
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to reserve.");
    }
  };

  /* ---- sync reservations ---- */
  const [syncReport, setSyncReport] = useState<SyncReservationsReport | null>(null);

  const handleSync = async (): Promise<void> => {
    try {
      const res = await syncReservations().unwrap();
      setSyncReport(res.report);
      toast.success(
        `Sync done — ${res.report.reserved.length} newly reserved, ` +
          `${res.report.alreadyReserved} already reserved, ` +
          `${res.report.conflicts.length} conflicts.`,
      );
    } catch (err: any) {
      toast.error(err?.data?.error || "Reservation sync failed.");
    }
  };

  /* ---- candidates ---- */
  const promote = async (emoji: string): Promise<void> => {
    const cat = window.prompt(`Add ${emoji} to the registry under which category?`, "Imported");
    if (!cat) return;
    try {
      await promoteCandidate({ emoji, category: cat.trim() }).unwrap();
      toast.success(`Promoted ${emoji} into the registry.`);
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to promote.");
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="mb-1 text-2xl font-bold">Emoji Registry</h1>
          <p className="text-sm text-gray-600">
            The platform-wide emoji catalog — signatures, reservations, and usage.
            {isFetching ? " · refreshing…" : ""}
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleSync} loading={syncing}>
          Sync reservations
        </Button>
      </div>

      {/* stats strip */}
      {stats && (
        <section className="mb-4 flex flex-wrap items-center gap-1.5">
          <StatPill label="total" value={stats.total} />
          <StatPill label="active" value={stats.active} className="bg-mint/30" />
          <StatPill label="retired" value={stats.retired} />
          <StatPill label="unclaimed" value={stats.unclaimed} />
          <StatPill label="signatures" value={stats.signatures} className="bg-sky/30" />
          <StatPill label="reserved" value={stats.reserved} className="bg-lemon/40" />
          {Object.entries(stats.reservedByKind)
            .filter(([, count]) => count > 0)
            .map(([kind, count]) => (
              <span
                key={kind}
                className="rounded-sm border border-ink/40 bg-lemon/20 px-1.5 py-0.5 text-[11px] text-gray-700"
                title={`reserved as ${kind}`}
              >
                {kind}: {count}
              </span>
            ))}
          <StatPill label="candidates" value={stats.candidates} />
        </section>
      )}

      {/* sync report */}
      {syncReport && (
        <section className="mb-6 rounded-sm border-brutal border-ink bg-mint/30 p-4 text-sm">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
              Reservation sync report
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setSyncReport(null)}>
              Dismiss
            </Button>
          </div>
          <p className="mb-2">
            <strong>{syncReport.reserved.length}</strong> newly reserved ·{" "}
            <strong>{syncReport.alreadyReserved}</strong> already reserved ·{" "}
            <strong>{syncReport.conflicts.length}</strong> conflicts ·{" "}
            <strong>{syncReport.notInRegistry.length}</strong> not in registry
          </p>
          {syncReport.reserved.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-semibold uppercase text-gray-500">Newly reserved</div>
              <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                {syncReport.reserved.map((r) => (
                  <li key={`${r.emoji}-${r.refId}`}>
                    <Emoji>{r.emoji}</Emoji>{" "}
                    <span className="text-gray-600">
                      {r.kind}: {r.label || r.refId}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {syncReport.conflicts.length > 0 && (
            <div className="mb-2">
              <div className="text-xs font-semibold uppercase text-pink">Conflicts</div>
              <ul className="mt-1 list-inside list-disc">
                {syncReport.conflicts.map((c) => (
                  <li key={`${c.emoji}-${c.refId}`}>
                    <Emoji>{c.emoji}</Emoji> ({c.refId}) — {c.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {syncReport.notInRegistry.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500">Not in registry</div>
              <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                {syncReport.notInRegistry.map((c) => (
                  <li key={`${c.emoji}-${c.refId}`}>
                    <Emoji>{c.emoji}</Emoji> <span className="text-gray-600">({c.refId})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* create */}
      <section className="mb-6 rounded-sm border-brutal border-ink bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Add emoji
        </h2>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col text-xs text-gray-600">
            Emoji
            <input
              value={newEmoji}
              onChange={(e) => setNewEmoji(e.target.value)}
              maxLength={8}
              className="mt-1 w-20 rounded-sm border-brutal border-ink px-2 py-1 text-lg"
              placeholder="🎯"
            />
          </label>
          <label className="flex flex-col text-xs text-gray-600">
            Category
            <input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="mt-1 w-44 rounded-sm border-brutal border-ink px-2 py-1 text-sm"
              placeholder="Symbols"
            />
          </label>
          <label className="flex flex-col text-xs text-gray-600">
            Name (optional)
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="mt-1 w-44 rounded-sm border-brutal border-ink px-2 py-1 text-sm"
              placeholder="direct hit"
            />
          </label>
          <Button variant="default" size="sm" onClick={handleCreate} loading={creating}>
            Add
          </Button>
        </div>
      </section>

      {/* candidates */}
      {candidates.length > 0 && (
        <section className="mb-6 rounded-sm border-brutal border-ink bg-lemon/30 p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600">
            Candidates seen in the wild ({candidates.length})
          </h2>
          <div className="flex flex-col gap-2">
            {candidates.map((c) => (
              <div key={c.emoji} className="flex items-center gap-3 text-sm">
                <Emoji scale={1.4}>{c.emoji}</Emoji>
                <span className="text-gray-600">
                  {c.count}× · {c.contexts.join(", ") || "—"}
                </span>
                <div className="ml-auto flex gap-2">
                  <Button variant="default" size="sm" onClick={() => promote(c.emoji)}>
                    Add to registry
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissCandidate({ emoji: c.emoji })}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search…"
          className="rounded-sm border-brutal border-ink px-3 py-1.5 text-sm"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as EmojiStatus | "")}
          className="rounded-sm border-brutal border-ink px-2 py-1.5 text-sm"
        >
          <option value="">all statuses</option>
          <option value="active">active</option>
          <option value="retired">retired</option>
        </select>
        <select
          value={state}
          onChange={(e) => setState(e.target.value as EmojiOwnershipState | "")}
          className="rounded-sm border-brutal border-ink px-2 py-1.5 text-sm"
        >
          <option value="">all ownership</option>
          <option value="unclaimed">unclaimed</option>
          <option value="signature">signature</option>
          <option value="reserved">reserved</option>
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-sm border-brutal border-ink px-2 py-1.5 text-sm"
        >
          <option value="">all categories</option>
          {categoryOptions.map((c) => (
            <option key={c.category} value={c.category}>
              {c.category} ({c.count})
            </option>
          ))}
        </select>
        <select
          value={reservedKind}
          onChange={(e) => setReservedKind(e.target.value as EmojiReservationKind | "")}
          className="rounded-sm border-brutal border-ink px-2 py-1.5 text-sm"
          title="Filter by reservation kind (most useful with ownership = reserved)"
        >
          <option value="">any reservation kind</option>
          {EMOJI_RESERVATION_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </div>

      {/* table */}
      {isLoading ? (
        <div className="p-4 text-sm text-gray-500">Loading…</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-sm border-brutal border-ink bg-white">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Emoji</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Reserved</th>
                  <th className="px-3 py-2">Uses</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr key={row.id} className="border-t border-ink/10">
                    <td className="px-3 py-1.5">
                      <Emoji scale={1.4}>{row.emoji}</Emoji>
                    </td>
                    <td className="px-3 py-1.5">{row.category}</td>
                    <td className="px-3 py-1.5">
                      <span
                        className={
                          row.status === "active" ? "text-green-700" : "text-gray-400 line-through"
                        }
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-1.5">
                      {row.ownership.state === "signature" ? (
                        <span>
                          {row.ownership.ownerUsername ?? "?"}{" "}
                          <span className="text-xs text-gray-400">(signature)</span>
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      {row.reservation ? (
                        <span
                          className="rounded-sm border border-ink/40 bg-lemon/30 px-1.5 py-0.5 text-xs"
                          title={row.reservation.refId ?? undefined}
                        >
                          {row.reservation.kind}
                          {row.reservation.label ? `: ${row.reservation.label}` : ""}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{row.usage.totalAllTime}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        <Button variant="ghost" size="sm" onClick={() => editCategory(row)}>
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => toggleStatus(row)}>
                          {row.status === "active" ? "Retire" : "Activate"}
                        </Button>
                        {row.ownership.state === "unclaimed" && (
                          <Button variant="ghost" size="sm" onClick={() => openReserve(row)}>
                            Reserve…
                          </Button>
                        )}
                        {row.ownership.state === "reserved" && (
                          <Button variant="ghost" size="sm" onClick={() => release(row)}>
                            Release
                          </Button>
                        )}
                        {row.ownership.state === "signature" && (
                          <Button variant="destructive" size="sm" onClick={() => forceRelease(row)}>
                            Force release
                          </Button>
                        )}
                        <Button variant="destructive" size="sm" onClick={() => remove(row)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                      No emojis match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* pagination */}
          <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
            <span>
              {from}–{to} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasPrev || isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Prev
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasNext || isFetching}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next →
              </Button>
            </div>
          </div>
        </>
      )}

      {/* reserve dialog */}
      {reserveTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
          onClick={() => setReserveTarget(null)}
        >
          <div
            className="w-full max-w-sm rounded-sm border-brutal border-ink bg-white p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600">
              Reserve <Emoji scale={1.4}>{reserveTarget.emoji}</Emoji>
            </h2>
            <div className="flex flex-col gap-2">
              <label className="flex flex-col text-xs text-gray-600">
                Kind
                <select
                  value={resKind}
                  onChange={(e) => setResKind(e.target.value as EmojiReservationKind)}
                  className="mt-1 rounded-sm border-brutal border-ink px-2 py-1.5 text-sm"
                >
                  {EMOJI_RESERVATION_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col text-xs text-gray-600">
                Ref id (optional)
                <input
                  value={resRefId}
                  onChange={(e) => setResRefId(e.target.value)}
                  className="mt-1 rounded-sm border-brutal border-ink px-2 py-1 text-sm"
                  placeholder="card slug / route / id"
                />
              </label>
              <label className="flex flex-col text-xs text-gray-600">
                Label (optional)
                <input
                  value={resLabel}
                  onChange={(e) => setResLabel(e.target.value)}
                  className="mt-1 rounded-sm border-brutal border-ink px-2 py-1 text-sm"
                  placeholder="Reclamation"
                />
              </label>
              <label className="flex flex-col text-xs text-gray-600">
                Note (optional)
                <input
                  value={resNote}
                  onChange={(e) => setResNote(e.target.value)}
                  className="mt-1 rounded-sm border-brutal border-ink px-2 py-1 text-sm"
                  placeholder="why this is reserved"
                />
              </label>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setReserveTarget(null)}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" onClick={submitReserve} loading={reserving}>
                  Reserve
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojisAdminPage;
